// ─────────────────────────────────────────────────────────────────────────────
//  leaderboard.js  —  Torch App · Leaderboard & User Stats
//
//  Ranking is now by `balance` (bankroll), not cumulative totalPoints.
//  totalPoints still tracked for all-time record keeping.
// ─────────────────────────────────────────────────────────────────────────────

import {
  getFirestore,
  doc, setDoc, getDoc, getDocs,
  collection, query, orderBy, limit,
} from "firebase/firestore";
import app from "./firebase";
import { getFunctions, httpsCallable } from "firebase/functions";

const db = getFirestore(app);

// ─── UPDATE USER STATS ────────────────────────────────────────────────────────
// SERVER-OWNED-BALANCES (fix 4.1 step 2, 2026-09-28): the same computation
// (W/L/pushes, streak, bestStreak, totalPoints from the user's picks) now runs
// in refreshMyStatsCallable and writes users/{uid} server-side, so these
// numbers can't be edited from the browser once the rules lock them.
export async function updateUserStats(userId, username) {
  if (!userId) return;
  try {
    const fn = httpsCallable(getFunctions(app), "refreshMyStatsCallable");
    await fn({ username: username || "Anonymous" });
  } catch (e) {
    console.error("[Leaderboard] updateUserStats failed:", e.message);
  }
}

// ─── LOAD LEADERBOARD (sorted by balance) ────────────────────────────────────
export async function loadLeaderboard(count = 50) {
  try {
    const q    = query(collection(db, "users"), orderBy("balance", "desc"), limit(count));
    const snap = await getDocs(q);
    return snap.docs.map((d, i) => ({
      id:          d.id,
      rank:        i + 1,
      username:    d.data().username    || "Anonymous",
      balance:     d.data().balance     ?? 0,
      totalPoints: d.data().totalPoints || 0,
      wins:        d.data().wins        || 0,
      losses:      d.data().losses      || 0,
      streak:      d.data().streak      || 0,
      totalPicks:  d.data().totalPicks  || 0,
    }));
  } catch (e) {
    console.error("[Leaderboard] loadLeaderboard failed:", e.message);
    return [];
  }
}

// ─── LOAD ONE USER STATS ──────────────────────────────────────────────────────
export async function loadUserStats(userId) {
  if (!userId) return null;
  try {
    const snap = await getDoc(doc(db, "users", userId));
    if (!snap.exists()) return null;
    return { id: userId, ...snap.data() };
  } catch (e) {
    console.error("[Leaderboard] loadUserStats failed:", e.message);
    return null;
  }
}
